import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";

import Highcharts from "highcharts";

// Components
import LeadershipChartRectangles from "./LeadershipChartRectangles";

// Hooks
import { useAppSelector } from "app/hooks";
import useResizeObserver from "use-resize-observer";

// Services
import { getPaceLabsLspm } from "store/slice/PaceLabs/paceLabsLSPM/paceLabsLSPMSlice";

// Styles
import theme from "assets/styles/themes";

// Utils
import { urlPaths } from "enums/urlPaths";
import { ChartRouteNames } from "enums/chartEnums";
import useAppziWidget from "utils/hooks/useAppziWidget";

// Types
import { TCardMessageProps } from "components/Message/CardMessage/CardMessage";

export type TLeadershipProfileCard = {
    title: string;
    content: string | React.ReactNode;
    popoverContent: { title: string; content: string };
    shouldShowLoading: boolean;
    onClickDetails: () => void;
    shouldShowBlurred?: boolean;
    bgColor?: string;
    alertMessage?: TCardMessageProps & {
        shouldShowMessage: boolean;
    };
};

const useLeadershipProfile = (): {
    leadershipProfileCards: TLeadershipProfileCard[];
    shouldStick: boolean;
    onClickMessageAction: () => void;
    cardsWrapperRef: React.RefObject<HTMLDivElement>;
    chartWrapperRef: (instance: HTMLElement | null) => void;
} => {
    const intl = useIntl();
    const navigate = useNavigate();

    const { handleOpenWidget } = useAppziWidget();

    const { data, status: requestInfo } = useAppSelector(getPaceLabsLspm);
    const { ref: chartWrapperRef, width } = useResizeObserver();

    const [shouldStick, setShouldStick] = useState(false);
    const cardsWrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleIntersection = (entries: IntersectionObserverEntry[]) => {
            const [entry] = entries;

            if (!entry?.isIntersecting) {
                setShouldStick(false);
                return;
            }

            setShouldStick(true);
        };

        const observer = new IntersectionObserver(handleIntersection, {
            rootMargin: "25% 0px 0px 0px",
            threshold: 0,
        });

        if (cardsWrapperRef.current) {
            observer.observe(cardsWrapperRef.current);
        }

        return () => observer.disconnect();
    }, []);

    // Call reflow to resize chart
    useEffect(() => {
        Highcharts.charts.forEach((chart, i) => {
            Highcharts.charts[i]?.reflow();
        });
    }, [width]);

    const onClickMessageAction = () => {
        navigate(urlPaths.PaceAssessment);
    };

    const leadershipProfileCards = [
        {
            title: "Situational",
            content: (
                <LeadershipChartRectangles
                    score={data?.lspm?.situation.total ?? 0}
                    labels={["Nascent", "Challenger"]}
                />
            ),
            popoverContent: {
                title: intl.formatMessage({ id: "paceLabsDashboard.leadership.popover.situational.title" }),
                content: intl.formatMessage({ id: "paceLabsDashboard.leadership.popover.situational.content" }),
            },
            shouldShowLoading: requestInfo === "pristine" || requestInfo === "fetching",
            shouldShowBlurred: requestInfo === "error",
            onClickDetails: () => navigate(`${urlPaths.PaceLabsInsights}/${ChartRouteNames.Situational}`),
            alertMessage: {
                shouldShowMessage: requestInfo === "error",
                title: intl.formatMessage({ id: "paceLabsDashboard.alert.message.situational.title" }),
                content: <FormattedMessage id="paceLabsDashboard.alert.message.situational.content" />,
                actions: [
                    {
                        label: intl.formatMessage({ id: "paceLabsDashboard.alert.message.situational.actionLabel" }),
                        onClickAction: handleOpenWidget,
                        buttonProps: {
                            variant: "outlined" as const,
                        },
                    },
                ],
            },
        },
        {
            title: "Domain",
            content: <LeadershipChartRectangles score={data?.lspm?.domain.total ?? 0} labels={["Nascent", "Expert"]} />,
            popoverContent: {
                title: intl.formatMessage({ id: "paceLabsDashboard.leadership.popover.domain.title" }),
                content: intl.formatMessage({ id: "paceLabsDashboard.leadership.popover.domain.content" }),
            },
            shouldShowLoading: requestInfo === "pristine" || requestInfo === "fetching",
            shouldShowBlurred: requestInfo === "error",
            onClickDetails: () => navigate(`${urlPaths.PaceLabsInsights}/${ChartRouteNames.Domain}`),
        },
        {
            title: "Functional",
            bgColor: theme.palette.background.green,
            content: data?.leadership_classification,
            popoverContent: {
                title: intl.formatMessage({ id: "paceLabsDashboard.leadership.popover.functional.title" }),
                content: intl.formatMessage({ id: "paceLabsDashboard.leadership.popover.functional.content" }),
            },
            shouldShowLoading: requestInfo === "pristine" || requestInfo === "fetching",
            shouldShowBlurred: requestInfo === "error",
            onClickDetails: () => navigate(`${urlPaths.PaceLabsInsights}/${ChartRouteNames.Balance}`),
        },
    ];

    return {
        leadershipProfileCards,
        shouldStick,
        onClickMessageAction,
        cardsWrapperRef,
        chartWrapperRef,
    };
};

export default useLeadershipProfile;
